import React, { useState } from "react";
import { Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useUser } from "@clerk/clerk-react";
import { createCheckout, downloadSweepPdf } from "@/lib/api";
import { useAcceptanceGate } from "@/components/legal/useAcceptanceGate";
import EmailGate from "@/components/EmailGate";
import MetricDefinitions from "@/components/MetricDefinitions";

// A$2,500 is the standard price; A$990 is the launch rate actually charged.
// Only the charged amount is ever passed to checkout - server.PACKAGES is the
// single source of what a card is debited, and it stays at 990.
const AUDIT_TIER = {
  id: "margin_audit",
  name: "Margin Audit",
  price: "A$990",
  standardPrice: "A$2,500",
  packageId: "margin_audit",
};

// Both brands shown together, not one brand's numbers in isolation - a
// measured figure only means something next to what the rival measures.
function CompareBar({ label, yourPct, rivalPct, yourLabel = "You", rivalLabel = "Competitor", suffix = "%" }) {
  const y = Math.max(0, Math.min(100, yourPct ?? 0));
  const r = Math.max(0, Math.min(100, rivalPct ?? 0));
  return (
    <div className="mb-4 last:mb-0">
      <div className="tick mb-2">{label}</div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-3">
          <span className="font-mono-tech text-[10px] text-[#525252] w-12 shrink-0 truncate" title={yourLabel}>{yourLabel}</span>
          <div className="flex-1 h-3 bg-[#dcd7ce] rounded-full overflow-hidden">
            <div className="h-full bg-[#171717] rounded-full" style={{ width: `${y}%` }} />
          </div>
          <span className="font-mono-tech text-[11px] text-[#171717] w-14 text-right shrink-0">
            {yourPct != null ? `${yourPct}${suffix}` : "n/a"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono-tech text-[10px] text-[#525252] w-12 shrink-0 truncate" title={rivalLabel}>{rivalLabel}</span>
          <div className="flex-1 h-3 bg-[#dcd7ce] rounded-full overflow-hidden">
            <div className="h-full bg-[#e88a51] rounded-full" style={{ width: `${r}%` }} />
          </div>
          <span className="font-mono-tech text-[11px] text-[#171717] w-14 text-right shrink-0">
            {rivalPct != null ? `${rivalPct}${suffix}` : "n/a"}
          </span>
        </div>
      </div>
    </div>
  );
}

// Why one store could not be read, phrased for the reader of a briefing rather
// than for the person who ran the sweep (server.py `_why_no_catalog`).
const PARTIAL_READ_REASON = {
  unresolved: "that domain did not resolve, so nothing could be read from it.",
  blocked: "the store refused our crawler, which is its own setting to make.",
  unreachable: "the store did not respond while this sweep ran.",
  no_catalog: "the store publishes no product feed we can read.",
  throttled: "the store asked us to slow down while this sweep ran, so we stopped reading it.",
};

function Section({ tag, title, subtitle, children, testid }) {
  return (
    <section data-testid={testid} className="card-cream">
      <header className="grid md:grid-cols-[220px_1fr] border-b border-[#ddd8ce]">
        <div className="p-6 md:p-8 border-r border-[#ddd8ce]">
          <div className="tick">{tag}</div>
        </div>
        <div className="p-6 md:p-8">
          <h3 className="font-serif-display text-2xl md:text-3xl text-[#171717] leading-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-3 font-mono-tech text-[12px] text-[#404040] max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      </header>
      <div className="p-6 md:p-8">{children}</div>
    </section>
  );
}

export default function ReportView({ report }) {
  const { modules = {}, headline, verdict, findings = [], locked_teasers = {}, analysis = {}, brands = {} } = report;
  const catalog = modules.catalog_density || {};
  const priceIdx = modules.price_index || { categories: [] };
  const unitEcon = modules.unit_economics || null;
  const coverage = report.data_coverage || {};
  // Which coverage definition the glossary should carry. A sampled read gets the
  // sampled definition - the one that explains why no product count appears.
  const coverageMode = JSON.stringify(coverage).toLowerCase().includes("sampled")
    ? "sampled"
    : "full";

  // Older reports (pre brand-naming) won't carry `brands` - fall back to the
  // old You/Rival convention rather than break.
  const yourBrand = brands.your_brand || { name: "your store", abbr: "You", domain: report.your_domain };
  const rivalBrand = brands.competitor || { name: "the competitor", abbr: "Competitor", domain: report.competitor_domain };
  const hasBrandKey = !!(brands.your_brand && brands.competitor);
  const yourRaw = analysis.your_brand || {};
  const rivalRaw = analysis.competitor || {};
  const yourBuckets = (yourRaw.discount || {}).depth_buckets || {};
  const rivalBuckets = (rivalRaw.discount || {}).depth_buckets || {};
  const hasDepthBuckets = Object.keys(yourBuckets).length > 0 && Object.keys(rivalBuckets).length > 0;
  const yourTiers = yourRaw.tiers || {};
  const rivalTiers = rivalRaw.tiers || {};
  const hasTiers = (yourTiers.gaps_pct || []).length > 0 && (rivalTiers.gaps_pct || []).length > 0;
  const DEPTH_LABELS = {
    shallow_1_15: "Shallow (under 15%)",
    mid_15_30: "Mid (15–30%)",
    deep_30_50: "Deep (30–50%)",
    clearance_50_plus: "Clearance (50%+)",
  };
  const sevColor = (s) => (s === "high" ? "text-[#9a3412] font-medium" : s === "medium" ? "text-[#8a5a3a]" : "text-[#404040]");
  const promColor = (p) => (p === "strong" ? "text-[#9a3412] font-medium" : p === "moderate" ? "text-[#8a5a3a]" : "text-[#404040]");

  // How many patterns cleared their thresholds, so the page can say "four of
  // nine" rather than implying four was all there was. Older reports predate
  // the field and fall back to the number shown.
  const considered = report.findings_considered ?? findings.length;

  // One gap per finding, in the same order and numbered to match. D3-style
  // findings whose answer is "nothing further" are dropped rather than padded -
  // a gap section listing a non-gap is exactly the filler this restructure
  // exists to remove.
  const gapItems = findings
    .map((f, i) => ({ ...f, index: i }))
    .filter((f) => f.cannot_tell && !/^nothing\b/i.test(f.cannot_tell.trim()));

  const hasVerdict = verdict && verdict !== "data_unavailable";

  // Stronger than the pricing page: /api/sweep/{id} answers 401 to a signed-out
  // visitor and 403 to any account that is not the owner, so a reader of THIS
  // page is always signed in. Asking for their email here could never be
  // justified - we know it, and the report they are reading is attached to it.
  const { user } = useUser();
  const accountEmail = user?.primaryEmailAddress?.emailAddress || null;
  const [gateOpen, setGateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { guard, gateNode } = useAcceptanceGate();

  const commission = async (email, country) => {
    try {
      setBusy(true);
      await guard("checkout", async () => {
        const { url } = await createCheckout(AUDIT_TIER.packageId, report.id, email, country);
        if (!url) throw new Error("No checkout url");
        window.location.href = url;
      });
      setBusy(false);
      setGateOpen(false);
    } catch (err) {
      // The market boundary is not a transient failure - retrying cannot
      // succeed, so "try again" would be untrue.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.error === "outside_supported_market") {
        toast.error(detail.message || "This product is sold in Australia only.");
        setBusy(false);
        return;
      }
      toast.error("Unable to start checkout. Please try again.");
      setBusy(false);
      setGateOpen(false);
    }
  };

  // "Copy report link" was removed deliberately, not lost.
  //
  // Once a sweep required an account, the report became owned: /api/sweep/{id}
  // answers 401 to a signed-out visitor and 403 to any account that is not the
  // owner. A copied URL therefore opened for exactly one person - the one who
  // already had it. The label promised sharing and delivered a dead end, and a
  // recipient who signed up out of curiosity hit 403 rather than the briefing.
  //
  // My Account lists every briefing, which is the honest route back to one. Real
  // sharing would need a signed, expiring token and a decision about handing a
  // third party a document derived from someone's storefront - a feature, not a
  // label change.
  //
  // Was window.print(), then window.open() at the PDF endpoint - which opened a
  // tab showing {"detail":"Not signed in"}, because a navigation carries no
  // Authorization header and that endpoint requires an account. Fetched with the
  // session token instead, and handed to the browser as a blob.
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      await downloadSweepPdf(report.id, `${report.your_domain || "margin"}-briefing.pdf`);
    } catch {
      // The PDF is typeset server-side and can take a moment; a silent failure
      // here reads as a dead button, which is how the previous bug survived.
      toast.error("Could not download the briefing. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div id="briefing" className="space-y-10 report-print" data-testid="report-view">
      {gateNode}

      {/* Stated once, at the top, and nowhere else. The per-module notes carry
          provenance — where a figure came from and what it cannot see. This is
          the separate claim neither those nor the page footers were making: that
          the document is analysis rather than advice. It sits on the deliverable
          because a client may share this with their accountant or advisers, who
          never passed the consent gate. Repeated on every section it would
          become furniture; here it is read once. */}
      <p
        data-testid="report-advice-disclaimer"
        className="font-mono-tech text-[11px] text-[#525252] leading-relaxed border-l-2 border-[#9a3412]/40 pl-3"
      >
        The contents of this report are analysis only. The decision remains
        entirely with the client and their team.
      </p>

      {/* One store unreadable. Half a comparison presented as a whole is the
          quiet version of a fabricated figure: every comparative line below is
          missing, and without this the reader would take the silence for a
          finding. Stated at the top, before any number. */}
      {report.partial_read && (
        <div
          data-testid="report-partial-read"
          className="card-cream p-5 border-l-2 border-[#9a3412]"
        >
          <div className="tick mb-2 text-[#9a3412]">Read one store only</div>
          <p className="font-mono-tech text-[12px] text-[#404040] leading-relaxed">
            <span className="text-[#171717] font-medium">{report.partial_read.domain}</span>
            {" — "}
            {PARTIAL_READ_REASON[report.partial_read.reason] || PARTIAL_READ_REASON.no_catalog}
            {" "}
            Every comparison in this briefing therefore covers{" "}
            {report.partial_read.side === "competitor"
              ? "your store alone, with no competitor line."
              : "the competitor alone, with no line for your own store."}
          </p>
        </div>
      )}
      <div className="card-cream flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 no-print" data-testid="report-toolbar">
        <div className="flex items-center gap-3">
          <span className="tick">Report · {report?.id?.slice(0, 8) ?? "unknown"}</span>
          <span className="tick hidden md:inline">Saved to the archive</span>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-primary" onClick={downloadPdf} disabled={pdfBusy}
                  data-testid="report-download-pdf">
            <Download size={14} /> {pdfBusy ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </div>

      {hasBrandKey && (
        <div className="card-tint px-6 py-4 flex flex-wrap items-center gap-x-8 gap-y-2 no-print" data-testid="brand-key">
          <span className="tick shrink-0">Reading this briefing</span>
          <span className="font-mono-tech text-[12px] text-[#171717]">
            <b>{yourBrand.abbr}</b> = {yourBrand.name} <span className="text-[#525252]">({yourBrand.domain})</span>
          </span>
          <span className="font-mono-tech text-[12px] text-[#171717]">
            <b>{rivalBrand.abbr}</b> = {rivalBrand.name} <span className="text-[#525252]">({rivalBrand.domain})</span>
          </span>
        </div>
      )}

      {/* One opening, not two.
          The briefing used to run "The call" and then "Editorial briefing" -
          two ~150-word prose blocks saying substantially the same thing, both
          before the reader reached a single finding. It also opened in the
          register of an analyst talking to another analyst: "price ladder
          collapses at the top", "with mix shift closed as a lever",
          "structurally reliant". The operator read it and could not follow it,
          which is the only test that matters for a document whose whole job is
          to make a founder care.

          Now: one plain sentence a reader gets on the first pass, then short
          supporting sentences. The model is held to roughly 12-18 words a
          sentence and an explicit list of banned analyst vocabulary. The CTA
          that used to sit here has moved to the end, after the gap section. */}
      {(headline || hasVerdict) && (
        <section className="card-cream p-8 md:p-10" data-testid="report-verdict">
          <div className="flex items-center justify-between mb-6">
            <div className="tick">In one line</div>
            <div className="tick">{yourBrand.abbr} <span className="text-[#525252]">vs</span> {rivalBrand.abbr}</div>
          </div>

          {headline && (
            <p className="font-serif-display text-[28px] md:text-[38px] text-[#171717] leading-[1.2] max-w-4xl"
               data-testid="report-headline-text">
              {headline}
            </p>
          )}

          {hasVerdict && (
            <div className={headline ? "mt-7 pt-7 border-t border-[#ddd8ce]" : ""}>
              <div className="tick mb-3">What we saw</div>
              <p className="font-mono-tech text-[14px] md:text-[15px] text-[#404040] leading-[1.75] max-w-3xl"
                 data-testid="report-verdict-text">
                {verdict}
              </p>
            </div>
          )}
        </section>
      )}

      {findings.length > 0 && (
        <Section
          testid="findings-register"
          tag={findings.length === 1 ? "What we found" : `What we found · ${findings.length}`}
          title={
            findings.length === 1
              ? "One thing worth your attention"
              : `${["", "One", "Two", "Three", "Four"][findings.length] || findings.length} things worth your attention`
          }
          subtitle={
            "The sweep reads what your store publishes, so it can see structure, not "
            + "sales. “How strongly it shows” is how clearly the pattern stands "
            + "out in that data — it is not a dollar figure, because a catalogue "
            + "cannot tell us what sells."
            + (considered > findings.length
                ? ` ${considered} patterns cleared their thresholds; these are the ones that carry the most.`
                : "")
          }
        >
          <div className="space-y-4">
            {findings.map((f, i) => (
              <div key={f.code || i} className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-5" data-testid={`finding-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="tick text-[#9a3412]">{String(i + 1).padStart(2, "0")}</span>
                    <h4 className="font-serif-display text-lg text-[#171717]">{f.title}</h4>
                  </div>
                  {/* Prominence, not severity. Severity claimed to be "the size
                      of the exposure", and size needs units sold - which a
                      catalogue read cannot see. This says how strongly the
                      pattern shows, which is what was actually measured. */}
                  <span className={`tick uppercase shrink-0 ${promColor(f.prominence)}`}>
                    {f.prominence} signal
                  </span>
                </div>
                <p className="font-mono-tech text-[13px] text-[#404040] leading-relaxed mb-2">{f.observation}</p>
                <p className="font-mono-tech text-[12px] text-[#525252] leading-relaxed">{f.implication}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* The gap section, and the reason the audit exists. Each line is paired
          to the finding above it rather than being a generic disclaimer - a
          buyer should be able to see exactly which question their money
          answers. This is also where the CTA belongs: after the reader knows
          what is missing, not interrupting the analysis to sell mid-paragraph. */}
      {gapItems.length > 0 && (
        <Section
          testid="cannot-tell"
          tag="The limit of public data"
          title="What this sweep cannot tell you"
          subtitle="Everything above was measured from what your store publishes. These are the questions that need your own numbers, listed against the finding each one belongs to."
        >
          <div className="space-y-4">
            {gapItems.map((g, i) => (
              <div key={g.code || i} className="flex flex-col md:flex-row gap-2 md:gap-6 pb-4 border-b border-[#e8e4df] last:border-0 last:pb-0">
                <div className="md:w-[38%] shrink-0">
                  <span className="tick text-[#9a3412]">{String(g.index + 1).padStart(2, "0")}</span>
                  <p className="font-mono-tech text-[12px] text-[#404040] leading-relaxed mt-1">{g.title}</p>
                </div>
                <p className="font-mono-tech text-[12px] text-[#525252] leading-relaxed flex-1">{g.cannot_tell}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-8 border-t border-[#ddd8ce] grid md:grid-cols-[1fr_auto] gap-6 md:items-end">
            <p className="font-mono-tech text-[13px] text-[#404040] leading-relaxed max-w-2xl">
              The Margin Audit answers these against your actual P&amp;L and
              returns the dollar figure, the sequence, and the decision. It is
              the reading; this was the reconnaissance.
            </p>
            <button
              className="btn-primary whitespace-nowrap"
              // Always the gate, even for a signed-in owner. The comment above
              // is right that we already know their EMAIL - but the Margin
              // Audit is sold in Australia only and nothing here knows where
              // they trade. Skipping the modal would send a foreign buyer to a
              // checkout the backend refuses, with no explanation of why.
              onClick={() => setGateOpen(true)}
              disabled={busy}
              data-testid="report-headline-cta"
            >
              {busy ? "Opening Stripe…" : (
                <>Commission the Margin Audit — {AUDIT_TIER.price} <span className="opacity-60 line-through ml-1">{AUDIT_TIER.standardPrice}</span> <ArrowRight size={14} /></>
              )}
            </button>
          </div>
        </Section>
      )}

      {/* Module 01 "Margin Risk Signals" was removed here. It restated the
          same items the findings above already carry, in the model's words
          rather than computed ones, and attached a severity the model chose -
          so the briefing said everything twice and the second telling was the
          less reliable one. Its data-coverage line survives, on Module 01
          below, because that was a measurement rather than prose. */}


      <Section testid="module-catalog" tag="Module 01" title={catalog.title || "Catalog Variation Density Mapping"} subtitle="Measured directly from both stores' live public product feeds. Variant density and sold-out share indicate cash trapped in the catalog.">
        <div className="grid md:grid-cols-2 gap-6">
          {[[yourBrand.abbr, catalog.your_brand, "catalog-your"], [rivalBrand.abbr, catalog.competitor || catalog.lean_cohort, "catalog-rival"]].map(([label, d, tid]) => (
            <div key={tid} className="panel-inset border border-[#ddd8ce] p-6 bg-[#eeeae2]" data-testid={tid}>
              <div className="tick mb-4">{label}</div>
              {d && d !== "data_unavailable" ? (
                <dl className="space-y-3 font-mono-tech text-[13px]">
                  <div className="flex justify-between border-b border-[#e8e4df] pb-2"><dt className="text-[#525252]">Products</dt><dd className="text-[#171717]">{d.products ?? d.skus ?? "—"}</dd></div>
                  <div className="flex justify-between border-b border-[#e8e4df] pb-2"><dt className="text-[#525252]">Variants</dt><dd className="text-[#171717]">{d.variants ?? "—"}</dd></div>
                  <div className="flex justify-between border-b border-[#e8e4df] pb-2"><dt className="text-[#525252]">Variants / Product</dt><dd className="text-[#171717]">{d.variants_per_product ?? "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#525252]">Sold-out Variant Share</dt><dd className="text-[#9a3412]">{d.sold_out_variant_share_pct != null ? `${d.sold_out_variant_share_pct}%` : "n/a"}</dd></div>
                </dl>
              ) : (
                <p className="font-mono-tech text-[12px] text-[#525252]">Public feed unavailable for this store — catalog metrics could not be measured.</p>
              )}
            </div>
          ))}
        </div>
        {catalog.verdict && catalog.verdict !== "data_unavailable" && (
          <div className="mt-6 pt-6 border-t border-[#ddd8ce]">
            <div className="tick mb-2">Verdict</div>
            <p className="font-serif-display text-xl text-[#171717] leading-snug max-w-3xl">{catalog.verdict}</p>
          </div>
        )}
        {(coverage.your_brand || coverage.competitor) && (
          <p className="mt-6 pt-6 border-t border-[#ddd8ce] font-mono-tech text-[11px] text-[#525252]">
            Data coverage — {yourBrand.abbr}: {coverage.your_brand || "n/a"} · {rivalBrand.abbr}: {coverage.competitor || "n/a"}
          </p>
        )}
      </Section>

      <Section testid="module-price" tag="Module 02" title={priceIdx.title || "Retail Price Positioning"} subtitle="Median and quartile shelf prices measured from both live feeds, with on-sale share and discount depth. Prices shown in each store's own currency.">
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {[[yourBrand.abbr, priceIdx.your_brand], [rivalBrand.abbr, priceIdx.competitor]].map(([label, d]) => (
            <div key={label} className="panel-inset border border-[#ddd8ce] p-6 bg-[#eeeae2]">
              <div className="tick mb-4">{label}</div>
              {d && d !== "data_unavailable" ? (
                <dl className="space-y-3 font-mono-tech text-[13px]">
                  <div className="flex justify-between border-b border-[#e8e4df] pb-2"><dt className="text-[#525252]">Median Price</dt><dd className="text-[#171717]">{d.median}</dd></div>
                  <div className="flex justify-between border-b border-[#e8e4df] pb-2"><dt className="text-[#525252]">P25 – P75</dt><dd className="text-[#171717]">{d.p25} – {d.p75}</dd></div>
                  <div className="flex justify-between border-b border-[#e8e4df] pb-2"><dt className="text-[#525252]">On-Sale Share</dt><dd className="text-[#9a3412]">{d.on_sale_share_pct}%</dd></div>
                  <div className="flex justify-between"><dt className="text-[#525252]">Avg Discount Depth</dt><dd className="text-[#9a3412]">{d.avg_discount_depth_pct}%</dd></div>
                </dl>
              ) : (
                <p className="font-mono-tech text-[12px] text-[#525252]">Public feed unavailable — price metrics could not be measured.</p>
              )}
            </div>
          ))}
        </div>

        {(priceIdx.ladder || []).length > 0 && (
          <div className="mb-6 pt-6 border-t border-[#ddd8ce]" data-testid="price-ladder-chart">
            <div className="tick mb-4">Price ladder — share of range by band, both stores</div>
            {priceIdx.ladder.map((b, i) => (
              <CompareBar
                key={i}
                label={`${priceIdx.your_brand?.currency || ""} ${b.low.toLocaleString()} – ${b.high.toLocaleString()}`}
                yourPct={b.your_share_pct}
                rivalPct={b.rival_share_pct}
                yourLabel={yourBrand.abbr}
                rivalLabel={rivalBrand.abbr}
              />
            ))}
          </div>
        )}

        {/* A chart that is missing for a reason has to say the reason. Without
            this the section simply vanishes and the reader cannot tell a
            declined comparison from a broken one. */}
        {(priceIdx.ladder || []).length === 0 && priceIdx.ladder_unavailable && (
          <div className="mb-6 pt-6 border-t border-[#ddd8ce]" data-testid="price-ladder-unavailable">
            <div className="tick mb-3">Price ladder — not shown</div>
            <p className="font-mono-tech text-[12px] text-[#404040] leading-relaxed max-w-3xl">
              The two stores price in {priceIdx.ladder_unavailable}, so their
              prices cannot be placed on one axis. We do not convert: a
              spot rate is a guess about a single day, and a converted figure is
              not a price either store charges anyone. The discount, availability
              and range-density comparisons below are unaffected — they are ratios
              within each store, so they carry no currency.
            </p>
          </div>
        )}

        {hasTiers && (
          <div className="mb-6 pt-6 border-t border-[#ddd8ce]" data-testid="tier-separation-chart">
            <div className="tick mb-4">Good–better–best tier separation, both stores</div>
            <p className="font-mono-tech text-[11px] text-[#525252] mb-4">
              Gap between the 20th/50th and 50th/80th percentile prices. Retail convention targets
              roughly {yourTiers.target_pct || 40}% — tighter than that and the tiers stop reading as different products.
            </p>
            <CompareBar
              label="20th → 50th percentile gap"
              yourPct={yourTiers.gaps_pct?.[0]}
              rivalPct={rivalTiers.gaps_pct?.[0]}
              yourLabel={yourBrand.abbr}
              rivalLabel={rivalBrand.abbr}
            />
            <CompareBar
              label="50th → 80th percentile gap"
              yourPct={yourTiers.gaps_pct?.[1]}
              rivalPct={rivalTiers.gaps_pct?.[1]}
              yourLabel={yourBrand.abbr}
              rivalLabel={rivalBrand.abbr}
            />
          </div>
        )}

        {hasDepthBuckets && (
          <div className="mb-6 pt-6 border-t border-[#ddd8ce]" data-testid="discount-depth-chart">
            <div className="tick mb-4">Discount depth as a share of the full catalog, both stores</div>
            <p className="font-mono-tech text-[11px] text-[#525252] mb-4">
              These five bars sum to 100% of each store's priced variants — most of a
              range is usually not on sale at all, so "full price" is shown alongside
              the discount bands rather than left implicit.
            </p>
            {Object.entries(DEPTH_LABELS).map(([key, label]) => (
              <CompareBar
                key={key}
                label={label}
                yourPct={yourBuckets[key]?.share_of_all_pct ?? 0}
                rivalPct={rivalBuckets[key]?.share_of_all_pct ?? 0}
                yourLabel={yourBrand.abbr}
                rivalLabel={rivalBrand.abbr}
              />
            ))}
            <CompareBar
              label="Full price (not on sale)"
              yourPct={Math.max(0, Math.round((100 - (yourRaw.discount?.breadth_pct ?? 0)) * 10) / 10)}
              rivalPct={Math.max(0, Math.round((100 - (rivalRaw.discount?.breadth_pct ?? 0)) * 10) / 10)}
              yourLabel={yourBrand.abbr}
              rivalLabel={rivalBrand.abbr}
            />
          </div>
        )}

        {/* Categories come from product titles read against a fixed vocabulary,
            not from the two stores' own `product_type` labels. Joining on those
            returned "no matching category in competitor feed" on every row for
            any real pair of competitors - see backend/product_categories.py.
            A row appears only where BOTH stores carry enough SKUs, so every row
            here is a comparison the reader can actually act on. */}
        {(priceIdx.categories || []).length > 0 && (
          <div className="pt-6 border-t border-[#ddd8ce]" data-testid="price-categories">
            <div className="tick mb-3">
              Like-for-like categories
              {priceIdx.categories_coverage_pct != null && (
                <span className="ml-2 normal-case text-[#525252]">
                  read from product titles · {priceIdx.categories_coverage_pct}% of the
                  smaller catalogue could be classified
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full font-mono-tech text-[12px]">
                <thead>
                  <tr className="text-left border-b border-[#ddd8ce]">
                    <th className="py-3 pr-6 tick">Category</th>
                    <th className="py-3 pr-6 tick">{yourBrand.abbr} Median</th>
                    <th className="py-3 pr-6 tick">{rivalBrand.abbr} Median</th>
                    <th className="py-3 pr-6 tick">{yourBrand.abbr} SKUs</th>
                    <th className="py-3 pr-6 tick">{rivalBrand.abbr} SKUs</th>
                    <th className="py-3 pr-6 tick">Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {priceIdx.categories.map((r, i) => (
                    <tr key={i} className="border-b border-[#e8e4df]">
                      <td className="py-4 pr-6 text-[#171717]">{r.category}</td>
                      <td className="py-4 pr-6 text-[#404040]">{r.your_median ?? "—"}</td>
                      <td className="py-4 pr-6 text-[#404040]">{r.competitor_median ?? "—"}</td>
                      <td className="py-4 pr-6 text-[#404040]">{r.your_skus ?? "—"}</td>
                      <td className="py-4 pr-6 text-[#404040]">{r.competitor_skus ?? "—"}</td>
                      <td className="py-4 pr-6 text-[#404040]">{r.signal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {priceIdx.bundles && (
              <p className="mt-4 font-mono-tech text-[12px] text-[#525252] leading-relaxed max-w-3xl">
                Bundles and sets: {yourBrand.abbr} {priceIdx.bundles.your_skus},{" "}
                {rivalBrand.abbr} {priceIdx.bundles.competitor_skus}. {priceIdx.bundles.note}
              </p>
            )}
          </div>
        )}

        {(priceIdx.categories || []).length === 0 && priceIdx.categories_unavailable && (
          <div className="pt-6 border-t border-[#ddd8ce]" data-testid="price-categories-unavailable">
            <div className="tick mb-3">Like-for-like categories — not shown</div>
            <p className="font-mono-tech text-[12px] text-[#404040] leading-relaxed max-w-3xl">
              {priceIdx.categories_unavailable}. We would rather show nothing here
              than a table of blanks: a category comparison is only worth reading
              when both stores carry enough of the same thing to compare.
            </p>
          </div>
        )}
        {priceIdx.verdict && priceIdx.verdict !== "data_unavailable" && (
          <div className="mt-6 pt-6 border-t border-[#ddd8ce]">
            <div className="tick mb-2">Verdict</div>
            <p className="font-serif-display text-xl text-[#171717] leading-snug max-w-3xl">{priceIdx.verdict}</p>
          </div>
        )}
      </Section>

      {/* Module 04 — unit & pack economics. Every figure here carries the share
          of the catalogue its size could be read from, because titles are
          written by people and the parse is never complete. Where a store was
          sampled rather than fully read, the panel says so instead of showing
          an empty table that would read as "nothing found". */}
      {unitEcon && (
        <Section
          testid="module-unit"
          tag="Module 03"
          title={unitEcon.title || "Unit & Pack Economics"}
          subtitle="Price per millilitre, gram or item — read from the sizes and pack counts written into product titles. Headline price and unit price can point in opposite directions."
        >
          <div className="grid md:grid-cols-2 gap-6">
            {[[yourBrand.abbr, unitEcon.your_brand, "unit-your"], [rivalBrand.abbr, unitEcon.competitor, "unit-rival"]].map(([label, d, tid]) => (
              <div key={tid} className="panel-inset border border-[#ddd8ce] p-6 bg-[#eeeae2]" data-testid={tid}>
                <div className="tick mb-4">{label}</div>
                {!d || d === "data_unavailable" ? (
                  <p className="font-mono-tech text-[12px] text-[#525252]">Public feed unavailable for this store — unit economics could not be measured.</p>
                ) : d.status === "not_available" ? (
                  <p className="font-mono-tech text-[12px] text-[#525252]">{d.why}</p>
                ) : (
                  <>
                    <dl className="space-y-3 font-mono-tech text-[13px]">
                      <div className="flex justify-between border-b border-[#e8e4df] pb-2">
                        <dt className="text-[#525252]">Single units</dt>
                        <dd className="text-[#171717]">{d.single_pct != null ? `${d.single_pct}%` : "—"}</dd>
                      </div>
                      <div className="flex justify-between border-b border-[#e8e4df] pb-2">
                        <dt className="text-[#525252]">Multipacks</dt>
                        <dd className="text-[#171717]">{d.multipack_pct != null ? `${d.multipack_pct}%` : "—"}</dd>
                      </div>
                      {/* Without this row the mix does not sum and the panel
                          contradicts the rest of the briefing. frank body reads
                          80.8% single and 0% multipack, so a fifth of the range
                          vanished - the Kits, which the category table lists at
                          20 SKUs. A kit is deliberately NOT a multipack (three
                          of one scent and three different scents are not
                          distinguishable from a title, per the pack rules), so
                          both figures were right and the panel simply never
                          showed where the difference went. */}
                      {d.bundle_pct != null && d.bundle_pct > 0 && (
                        <div className="flex justify-between border-b border-[#e8e4df] pb-2">
                          <dt className="text-[#525252]">Kits &amp; bundles</dt>
                          <dd className="text-[#171717]">{d.bundle_pct}%</dd>
                        </div>
                      )}
                      {/* These two only appear when there was something to
                          check. "none comparable" and "none checkable" next to
                          "0%" filled the panel with four rows that each said
                          nothing, which reads as a broken module rather than a
                          catalogue that names no sizes. A row with no
                          measurement behind it is not evidence of anything. */}
                      {d.multipacks_compared > 0 && (
                        <div className="flex justify-between border-b border-[#e8e4df] pb-2">
                          <dt className="text-[#525252]">Packs above their own single</dt>
                          <dd className={d.multipacks_priced_above_singles ? "text-[#9a3412]" : "text-[#171717]"}>
                            {d.multipacks_priced_above_singles} of {d.multipacks_compared}
                          </dd>
                        </div>
                      )}
                      {d.size_ladders_checked > 0 && (
                        <div className="flex justify-between">
                          <dt className="text-[#525252]">Size-ladder inversions</dt>
                          <dd className={d.size_inversions ? "text-[#9a3412]" : "text-[#171717]"}>
                            {d.size_inversions} of {d.size_ladders_checked}
                          </dd>
                        </div>
                      )}
                    </dl>

                    {d.unit_prices && d.unit_prices !== "data_unavailable" ? (
                      <div className="mt-5 pt-5 border-t border-[#e8e4df]">
                        <div className="tick mb-3">Median price per unit</div>
                        <dl className="space-y-2 font-mono-tech text-[13px]">
                          {Object.entries(d.unit_prices).map(([fam, v]) => (
                            <div key={fam} className="flex justify-between">
                              <dt className="text-[#525252]">per {v.per}</dt>
                              <dd className="text-[#171717]">{v.median}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ) : (
                      <p className="mt-5 pt-5 border-t border-[#e8e4df] font-mono-tech text-[12px] text-[#525252]">
                        {d.why_no_unit_prices}
                      </p>
                    )}

                    {/* Suppressed at zero: the sentence above already says the
                        titles carry no sizes, and "Sizes read from 0% of this
                        catalogue's titles" underneath it said the same thing a
                        second time in a way that read as an error code. */}
                    {d.unit_coverage_pct != null && d.unit_coverage_pct > 0 && (
                      <p className="mt-4 font-mono-tech text-[11px] text-[#737373]">
                        Sizes read from {d.unit_coverage_pct}% of this catalogue's titles.
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          {unitEcon.verdict && unitEcon.verdict !== "data_unavailable" && (
            <div className="mt-6 pt-6 border-t border-[#ddd8ce]">
              <div className="tick mb-2">Verdict</div>
              <p className="font-serif-display text-xl text-[#171717] leading-snug max-w-3xl">{unitEcon.verdict}</p>
            </div>
          )}
        </Section>
      )}

      {/* The second glossary was deleted here.

          The briefing shipped two: this one ("What each measurement means") and
          MetricDefinitions below ("Every term in this briefing, in plain
          English", 30 terms). Two glossaries in one document means neither is
          the reference, and they can disagree - which is precisely what
          metric_definitions.py exists to prevent, since it is the single source
          the endpoint, both PDFs and the report view all read from.

          This copy was hand-written in JSX and shared no source with it. */}

      {locked_teasers && Object.keys(locked_teasers).length > 0 && (
        <section className="card-cream p-6 md:p-8" data-testid="report-teasers">
          <div className="tick mb-2 text-[#9a3412]">Not answerable from public data</div>
          <p className="font-mono-tech text-[12px] text-[#525252] mb-4 max-w-2xl leading-relaxed">
            These need your P&amp;L or ongoing tracking. The free sweep does not measure them,
            and nothing below is a finding - it is the scope of the paid audit.
          </p>
          <div className="grid md:grid-cols-2 gap-4 stagger-children">
            {Object.entries(locked_teasers).map(([k, v]) => (
              <div key={k} className="panel-inset border border-[#ddd8ce] bg-[#eeeae2] p-4">
                <div className="tick mb-2">{k}</div>
                <p className="font-mono-tech text-[12px] text-[#404040] leading-relaxed">{v}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <MetricDefinitions scope="sweep" coverage={coverageMode} />

      <EmailGate
        open={gateOpen}
        onOpenChange={setGateOpen}
        tier={AUDIT_TIER}
        onSubmit={commission}
        loading={busy}
        askCountry
        initialEmail={accountEmail || ""}
      />
    </div>
  );
}
